import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Input, Select, Row, Col, Tooltip, Modal, message } from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  FileOutlined, 
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { getOfflineBookingPayment } from '../../../../api/commonAPI';
import '../../../../assets/scss/components/OfflineBooking/BookingsList.scss';

const { Search } = Input;
const { Option } = Select;

const BookingsList = ({ bookings, loading, onEdit, onDelete, onRefresh, onConfirmBooking, onCancelBooking }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  
  // Get bookings directly from Redux state to ensure we have the latest data
  const reduxBookings = useSelector(state => state.offlineBooking?.bookings || []);
  
  // Use Redux bookings if available, otherwise fall back to props
  const displayBookings = reduxBookings.length > 0 ? reduxBookings : bookings;
  
  const [paymentData, setPaymentData] = useState({});
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('ALL');

  // Filter only OFFLINE_BOOKING type
  const offlineBookings = displayBookings.filter(booking => booking.bookingType === 'OFFLINE_BOOKING');

  // Apply search and filters
  const filteredBookings = offlineBookings.filter(booking => {
    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      const matchesSearch = 
        booking.bookingRef?.toLowerCase().includes(searchLower) ||
        booking.user?.name?.toLowerCase().includes(searchLower) ||
        booking.user?.email?.toLowerCase().includes(searchLower) ||
        booking.clientName?.toLowerCase().includes(searchLower) ||
        booking.clientEmail?.toLowerCase().includes(searchLower) ||
        booking.title?.toLowerCase().includes(searchLower) ||
        booking.spaceName?.toLowerCase().includes(searchLower) ||
        booking.space?.spaceName?.toLowerCase().includes(searchLower) ||
        booking.space?.name?.toLowerCase().includes(searchLower) ||
        booking.id?.toLowerCase().includes(searchLower) ||
        booking._id?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Booking status filter
    if (filterStatus !== 'ALL' && booking.bookingStatus !== filterStatus) {
      return false;
    }

    // Payment status filter
    if (filterPaymentStatus !== 'ALL') {
      const bookingId = booking.id || booking._id;
      const payment = paymentData[bookingId];
      const paymentStatus = payment?.paymentStatus || payment?.status;
      
      if (paymentStatus !== filterPaymentStatus) {
        return false;
      }
    }

    return true;
  });

  // Fetch payment status for all bookings
  useEffect(() => {
    const fetchPaymentStatuses = async () => {
      if (offlineBookings.length === 0) return;
      
      setLoadingPayments(true);
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const payments = {};

      try {
        // Fetch payment data for each booking
        await Promise.all(
          offlineBookings.map(async (booking) => {
            try {
              const response = await getOfflineBookingPayment(booking.id || booking._id, token);
              if (response) {
                payments[booking.id || booking._id] = response;
              }
            } catch (error) {
              // If payment not found, that's ok - some bookings might not have payment records yet
              console.log(`No payment found for booking ${booking.id}`);
            }
          })
        );

        setPaymentData(payments);
      } catch (error) {
        console.error('Error fetching payment statuses:', error);
      } finally {
        setLoadingPayments(false);
      }
    };

    fetchPaymentStatuses();
  }, [bookings]);



  // Get payment status tag
  const getPaymentStatusTag = (status) => {
    if (!status) return <Tag color="default">No Payment</Tag>;
    
    const statusConfig = {
      INITIATED: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Pending' },
      PENDING: { color: 'warning', icon: <ClockCircleOutlined />, text: 'Pending' },
      COMPLETED: { color: 'success', icon: <CheckCircleOutlined />, text: 'Paid' },
      SUCCESS: { color: 'success', icon: <CheckCircleOutlined />, text: 'Paid' },
      CANCELLED: { color: 'error', icon: <CloseCircleOutlined />, text: 'Cancelled' },
      FAILED: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' }
    };
    const config = statusConfig[status] || { color: 'default', icon: null, text: status };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  // Get booking status tag
  const getBookingStatusTag = (status) => {
    const colors = {
      INITIATED: 'blue',
      TENTATIVE: 'orange',
      CONFIRMED: 'green',
      COMPLETED: 'blue',
      CANCELLED: 'red',
      PENDING: 'orange',
      DECLINED: 'red',
      TERMINATED: 'default',
      WITHDRAWN: 'default'
    };
    return <Tag color={colors[status] || 'default'}>{status || 'INITIATED'}</Tag>;
  };
  
  const columns = [
    {
      title: 'Booking Ref',
      dataIndex: 'bookingRef',
      key: 'bookingRef',
      width: 120,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        const ref = text || record.bookingRef || record.id?.substring(0, 8) || record._id?.substring(0, 8) || '-';
        return (
          <Tooltip title="Click to view booking details">
            <Button 
              type="link" 
              size="small" 
              style={{ padding: 0 }}
              onClick={() => history.push(`/dashboard/offline-booking/view/${record.id || record._id}`)}
            >
              {ref}
            </Button>
          </Tooltip>
        );
      }
    },
     {
      title: 'Payment Ref',
      key: 'paymentRef',
      width: 110,
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        const bookingId = record.id || record._id;
        const payment = paymentData[bookingId];
        
        if (!payment) {
          return <span style={{ color: '#8c8c8c' }}>-</span>;
        }
        
        const paymentRef = payment.id?.substring(0, 8) || '-';
        
        return (
          <Tooltip title={payment.transactionReference ? `Transaction Ref: ${payment.transactionReference}` : `Payment ID: ${payment.id}`}>
            <span style={{ fontSize: '12px' }}>
              {paymentRef}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Client',
      key: 'client',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        const clientName = record.user?.name || record.clientName || 'N/A';
        const clientEmail = record.user?.email || record.clientEmail || '';
        return (
          <Tooltip title={`${clientName} - ${clientEmail}`}>
            <Space direction="vertical" size={0}>
              <span style={{ fontSize: '13px' }}><UserOutlined /> {clientName}</span>
              <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                {clientEmail.length > 20 ? clientEmail.substring(0, 20) + '...' : clientEmail}
              </span>
            </Space>
          </Tooltip>
        );
      }
    },
    {
      title: 'Space',
      dataIndex: 'title',
      key: 'spaceName',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        let spaceName = text || record.spaceName || record.space?.spaceName || record.space?.name;
        if (!spaceName && typeof record.space === 'string') {
          spaceName = `ID: ${record.space.substring(0, 8)}...`;
        }
        spaceName = spaceName || '-';
        
        return (
          <Tooltip title={spaceName}>
            <span>{spaceName}</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Booking Date',
      key: 'bookingDate',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: '13px' }}><CalendarOutlined /> {moment(record.bookingStartDate).format('DD MMM YYYY')}</span>
          <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {record.pricingOptions && record.pricingOptions.length > 0
              ? `${record.pricingOptions[0].startTime} - ${record.pricingOptions[0].endTime}`
              : `${moment(record.bookingStartDate).format('HH:mm')} - ${moment(record.bookingEndDate).format('HH:mm')}`}
          </span>
        </Space>
      )
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (_, record) => {
        const hasAdjustment = record.financialDetails?.isPriceAdjusted;
        const adjustmentAmount = record.financialDetails?.priceAdjustmentAmount || 0;
        
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <span style={{ fontWeight: 500 }}>
              {record.currency || 'LKR'} {record.totalPrice?.toLocaleString() || '0.00'}
            </span>
            {hasAdjustment && (
              <Tooltip title={`Price manually adjusted: ${adjustmentAmount >= 0 ? '+' : ''}${adjustmentAmount.toFixed(2)} ${record.currency || 'LKR'}`}>
                <span style={{ 
                  fontSize: '11px', 
                  color: adjustmentAmount >= 0 ? '#52c41a' : '#ff4d4f',
                  fontStyle: 'italic'
                }}>
                  {adjustmentAmount >= 0 ? '+' : ''}{adjustmentAmount.toFixed(2)}
                </span>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Booking Status',
      dataIndex: 'bookingStatus',
      key: 'bookingStatus',
      width: 120,
      align: 'center',
      render: (status) => getBookingStatusTag(status)
    },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const bookingId = record.id || record._id;
        const payment = paymentData[bookingId];
        const status = payment?.paymentStatus || payment?.status;
        return getPaymentStatusTag(status);
      }
    },
   
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      align: 'right',
      render: (_, record) => {
        const bookingId = record.id || record._id;
        const bookingStatus = record.bookingStatus;
        const isTentative = bookingStatus === 'TENTATIVE';
        const isConfirmed = bookingStatus === 'CONFIRMED';
        const isCancelled = bookingStatus === 'CANCELLED';
        const isWithdrawn = bookingStatus === 'WITHDRAWN';
        
        // Check payment status to disable edit for completed payments
        const payment = paymentData[bookingId];
        const paymentStatus = payment?.paymentStatus || payment?.status;
        const isPaymentCompleted = paymentStatus === 'COMPLETED' || paymentStatus === 'SUCCESS';
        
        return (
          <Space size="small">
            {/* Show Confirm button only for TENTATIVE bookings */}
            {isTentative && (
              <Tooltip title="Confirm Booking">
                <Button
                  type="link"
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => onConfirmBooking(record)}
                >
                </Button>
              </Tooltip>
            )}
            
            {/* Show Cancel button only for TENTATIVE or CONFIRMED bookings */}
            {(isTentative || isConfirmed) && !isCancelled && (
              <Tooltip title="Cancel Booking">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => onCancelBooking(record)}
                >
                </Button>
              </Tooltip>
            )}
            
            <Tooltip title={isPaymentCompleted ? "Cannot edit booking with completed payment" : isCancelled ? "Cannot edit cancelled booking" : isWithdrawn ? "Cannot edit withdrawn booking" : "Edit Booking"}>
              <Button 
                type="link" 
                size="small"
                icon={<EditOutlined />} 
                onClick={() => onEdit(record)}
                disabled={isCancelled || isPaymentCompleted || isWithdrawn}
              />
            </Tooltip>
            {/* Delete action disabled */}
            <Tooltip title="Delete Booking">
              <Button 
                type="link" 
                size="small"
                danger 
                icon={<DeleteOutlined />} 
                onClick={() => onDelete(record.id || record._id)}
                disabled
              />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  return (
    <>
      {/* Filters */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={10}>
          <Search
            placeholder="Search by booking ref, client name, or email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
            allowClear
            prefix={<SearchOutlined />}
          />
        </Col>
        <Col span={7}>
          <Select
            style={{ width: '100%' }}
            placeholder="Filter by Booking Status"
            value={filterStatus}
            onChange={setFilterStatus}
          >
            <Option value="ALL">All Booking Status</Option>
            <Option value="INITIATED">Initiated</Option>
            <Option value="TENTATIVE">Tentative (Pending Confirmation)</Option>
            <Option value="CONFIRMED">Confirmed</Option>
            <Option value="COMPLETED">Completed</Option>
            <Option value="CANCELLED">Cancelled</Option>
          </Select>
        </Col>
        <Col span={7}>
          <Select
            style={{ width: '100%' }}
            placeholder="Filter by Payment Status"
            value={filterPaymentStatus}
            onChange={setFilterPaymentStatus}
          >
            <Option value="ALL">All Payment Status</Option>
            <Option value="INITIATED">Pending Payment</Option>
            <Option value="COMPLETED">Paid</Option>
            <Option value="CANCELLED">Cancelled</Option>
          </Select>
        </Col>
      </Row>

      <Card 
        title={
          <span>
            Offline Bookings <span style={{ color: '#000' }}>({filteredBookings.length})</span>
          </span>
        }
        className="bookings-list-card"
        extra={
          <Button onClick={onRefresh} loading={loading} icon={<FileOutlined />}>
            Refresh List
          </Button>
        }
      >
        <Table 
          columns={columns} 
          dataSource={filteredBookings}
          rowKey={(record) => record.id || record._id}
          loading={loading || loadingPayments}
          className="bookings-table"
          scroll={{ x: 1280 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} offline bookings`
          }}
        />
      </Card>
    </>
  );
};

export default BookingsList;
